import { useState } from 'react';
import { formatarCno } from '@raiox/domain';
import { trpc } from '../lib/trpc.js';
import { paraApi, useFiltro, type Filtro } from '../estado/filtros.js';
import { Carregando, Erro, Vazio } from '../componentes/Estados.js';
import { Tile } from '../componentes/Tile.js';
import { data, inteiro, moeda, moedaOuTraco } from '../lib/formato.js';
import { nomeArquivo, urlExportacao } from '../lib/exportar.js';
import type { Obra } from '../lib/tipos.js';

const DOCS_POR_OBRA = 100;

function DocumentosDaObra({ filtro, cno }: { filtro: Filtro; cno: string }) {
  const consulta = trpc.documentos.listar.useQuery({
    filtro: { ...paraApi(filtro), cno },
    porPagina: DOCS_POR_OBRA,
  });

  if (consulta.isPending) {
    return (
      <div style={{ padding: 16 }}>
        <Carregando linhas={3} rotulo="Carregando os documentos da obra" />
      </div>
    );
  }
  if (consulta.error) {
    return (
      <div style={{ padding: 16 }}>
        <Erro erro={consulta.error} aoTentarDeNovo={() => void consulta.refetch()} />
      </div>
    );
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <table>
        <thead>
          <tr>
            <th>Tipo</th>
            <th className="r">Nº</th>
            <th>Emissão</th>
            <th>Emitente</th>
            <th className="r">Valor</th>
            <th className="r">ICMS</th>
            <th className="r">ISS</th>
            <th className="r">ISS ret.</th>
            <th className="r">INSS ret.</th>
            <th className="r">IRRF</th>
            <th className="r">CSLL</th>
            <th className="r">IBS</th>
            <th className="r">CBS</th>
          </tr>
        </thead>
        <tbody>
          {consulta.data.itens.map((doc) => (
            <tr key={doc.id}>
              <td>
                <span className="badge tipo">{doc.tipo}</span>
              </td>
              <td className="num r">{doc.numero || '—'}</td>
              <td className="num nowrap">{data(doc.dataEmissao)}</td>
              <td>
                <span className="trunc" title={doc.emitente.nome}>
                  {doc.emitente.nome || '—'}
                </span>
              </td>
              <td className="num r strong">{moeda(doc.valorTotal)}</td>
              <td className="num r">{moedaOuTraco(doc.tributos.icms)}</td>
              <td className="num r">{moedaOuTraco(doc.tributos.iss)}</td>
              <td className="num r">{moedaOuTraco(doc.retencoes.iss)}</td>
              <td className="num r">{moedaOuTraco(doc.retencoes.inss)}</td>
              <td className="num r">{moedaOuTraco(doc.retencoes.irrf)}</td>
              <td className="num r">{moedaOuTraco(doc.retencoes.csll)}</td>
              <td className="num r">{moedaOuTraco(doc.tributos.ibs)}</td>
              <td className="num r">{moedaOuTraco(doc.tributos.cbs)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {consulta.data.total > consulta.data.itens.length ? (
        <p style={{ margin: 0, padding: '10px 14px', fontSize: 11.5, color: 'var(--ink-3)' }}>
          Mostrando {inteiro(consulta.data.itens.length)} de {inteiro(consulta.data.total)} documentos desta
          obra. Use a exportação para a lista completa.
        </p>
      ) : null}
    </div>
  );
}

function CartaoObra({ obra, filtro, inicialAberto }: { obra: Obra; filtro: Filtro; inicialAberto: boolean }) {
  const [aberto, setAberto] = useState(inicialAberto);

  return (
    <div className="obra">
      <header>
        <div>
          <div className="cno">{formatarCno(obra.cno)}</div>
          <div className="meta">
            {inteiro(obra.documentos)} documento(s)
            {obra.art ? ` · ART ${obra.art}` : ''}
            {obra.descricao ? ` · ${obra.descricao}` : ''}
          </div>
        </div>
        <div className="kpis">
          <div className="kpi">
            <div className="l">Valor</div>
            <div className="v">{moeda(obra.valorTotal)}</div>
          </div>
          <div className="kpi">
            <div className="l">ISS retido</div>
            <div className="v">{moeda(obra.retIss)}</div>
          </div>
          <div className="kpi">
            <div className="l">INSS retido</div>
            <div className="v">{moeda(obra.retInss)}</div>
          </div>
          <div className="kpi">
            <div className="l">IBS + CBS</div>
            <div className="v">{moeda(obra.ibs + obra.cbs)}</div>
          </div>
          <button
            type="button"
            className="btn sm"
            aria-expanded={aberto}
            onClick={() => setAberto((a) => !a)}
          >
            {aberto ? 'Ocultar documentos' : 'Ver documentos'}
          </button>
        </div>
      </header>
      {aberto ? <DocumentosDaObra filtro={filtro} cno={obra.cno} /> : null}
    </div>
  );
}

export function Obras() {
  const { filtro } = useFiltro();
  const consulta = trpc.obras.listar.useQuery({ filtro: paraApi(filtro) });

  if (consulta.isPending) {
    return (
      <section className="panel">
        <Carregando linhas={5} rotulo="Carregando as obras" />
      </section>
    );
  }
  if (consulta.error) {
    return (
      <section className="panel">
        <Erro erro={consulta.error} aoTentarDeNovo={() => void consulta.refetch()} />
      </section>
    );
  }

  const { obras, semVinculo } = consulta.data;

  return (
    <section className="panel">
      <div className="toolbar">
        <div className="search" />
        <a className="btn sm" href={urlExportacao('obras', filtro)} download={nomeArquivo('obras')}>
          Exportar obras
        </a>
      </div>

      {obras.length === 0 ? (
        <Vazio titulo="Nenhum CNO encontrado">
          Nenhum dos XMLs desta seleção traz o código da obra — nem no campo estruturado (
          <span className="mono">cObra</span>, <span className="mono">CodigoObra</span>) nem no texto
          complementar.
        </Vazio>
      ) : (
        <>
          {obras.map((obra, indice) => (
            <CartaoObra key={obra.cno} obra={obra} filtro={filtro} inicialAberto={indice === 0} />
          ))}
          <p style={{ margin: '4px 0 0', fontSize: 11.5, color: 'var(--ink-3)', maxWidth: '92ch' }}>
            Uma nota pode citar mais de uma obra. Nesse caso o documento inteiro entra em cada CNO, então a
            soma das obras é maior que o total geral — de propósito.
          </p>
        </>
      )}

      {semVinculo.documentos > 0 ? (
        <>
          <div className="eyebrow">Sem vínculo com obra</div>
          <div className="tiles">
            <Tile
              rotulo="Valor sem CNO"
              valor={semVinculo.valorTotal}
              sub={`${inteiro(semVinculo.documentos)} documento(s)`}
            />
            <Tile
              rotulo="INSS retido sem CNO"
              valor={semVinculo.retInss}
              sub="não conciliável com obra"
            />
            <Tile rotulo="ISS retido sem CNO" valor={semVinculo.retIss} />
          </div>
        </>
      ) : null}
    </section>
  );
}
